import { actingFor, lipSync, voiceScript, type VoiceLine } from '@elena/shared';
import type { TalkingHead as TalkingHeadType } from '@met4citizen/talkinghead';

/**
 * One 3D Elena for the whole app. TalkingHead owns a WebGL context, so instead of one per page,
 * a single canvas lives in a host element that moves into whichever stage is on screen.
 * Speech playback (lib/speech.ts) announces each line on voiceScript; here it becomes visemes,
 * a mood, a facial expression and a gesture.
 */

export type AvatarView = 'head' | 'upper' | 'mid' | 'full';
export type AvatarMode = 'idle' | 'listening' | 'thinking' | 'speaking';

const MODEL = `${import.meta.env.BASE_URL}elena/elena-v3.glb`;

// Night lighting with the cyan rim from Elena's identity, chosen in the v3 prototype.
const LIGHTS = {
  lightAmbientColor: 0x5a6fa8,
  lightAmbientIntensity: 0.9,
  lightDirectColor: 0xaac4ff,
  lightDirectIntensity: 12,
  lightDirectPhi: 1.2,
  lightDirectTheta: 1.4,
  lightSpotColor: 0x5ee7ff,
  lightSpotIntensity: 30,
  lightSpotPhi: 0.1,
  lightSpotTheta: 4.2,
  lightSpotDispersion: 1,
};

// Honey blonde: the hair texture becomes brightness, then gets this dark→light ramp.
const HAIR_DARK = [0.22, 0.1, 0.03];
const HAIR_LIGHT = [0.85, 0.55, 0.22];

export function webglAvailable(): boolean {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch {
    return false;
  }
}

type Status = 'idle' | 'loading' | 'ready' | 'failed';
let status: Status = 'idle';
let progress = 0;
let head: TalkingHeadType | null = null;
let loading: Promise<void> | null = null;
const host = typeof document !== 'undefined' ? document.createElement('div') : null;
host?.classList.add('elena3d-host');

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const avatarStatus = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

let snapshot: { status: Status; progress: number } = { status, progress };
export const getAvatarSnapshot = () => {
  if (snapshot.status !== status || snapshot.progress !== progress) snapshot = { status, progress };
  return snapshot;
};

function tintHair(h: TalkingHeadType) {
  h.armature?.traverse((o) => {
    const mesh = o as { isMesh?: boolean; material?: unknown };
    if (!mesh.isMesh) return;
    for (const m of [mesh.material].flat() as Array<{
      name: string;
      map?: unknown;
      onBeforeCompile: (shader: {
        fragmentShader: string;
        uniforms: Record<string, unknown>;
      }) => void;
      needsUpdate: boolean;
    }>) {
      if (!/hair/i.test(m.name) || !m.map) continue;
      m.onBeforeCompile = (shader) => {
        shader.uniforms.hairDark = { value: HAIR_DARK };
        shader.uniforms.hairLight = { value: HAIR_LIGHT };
        shader.fragmentShader = shader.fragmentShader
          .replace(
            'void main() {',
            'uniform vec3 hairDark;\nuniform vec3 hairLight;\nvoid main() {',
          )
          .replace(
            '#include <map_fragment>',
            `#include <map_fragment>
            float hairL = dot(sampledDiffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
            diffuseColor.rgb = mix(hairDark, hairLight, smoothstep(0.0, 0.3, hairL));`,
          );
      };
      m.needsUpdate = true;
    }
  });
}

/** Loads three.js, TalkingHead and the model once; later calls share the same promise. */
export function loadAvatar(): Promise<void> {
  if (loading) return loading;
  if (!host || !webglAvailable()) {
    status = 'failed';
    emit();
    return Promise.resolve();
  }
  status = 'loading';
  emit();
  loading = (async () => {
    try {
      const { TalkingHead } = await import('@met4citizen/talkinghead');
      // Needs a size while it builds the renderer; the host is moved into a stage right after.
      if (!host.isConnected) {
        host.style.cssText = 'position:fixed;left:-9999px;width:400px;height:400px';
        document.body.appendChild(host);
      }
      head = new TalkingHead(host, {
        lipsyncModules: [],
        lipsyncLang: 'en',
        cameraView: 'upper',
        cameraRotateEnable: false,
        cameraZoomEnable: false,
        modelFPS: 60,
        modelPixelRatio: Math.min(1.5, 2 / window.devicePixelRatio) || 1,
        avatarIdleEyeContact: 0.5,
        avatarIdleHeadMove: 0.5,
        avatarSpeakingEyeContact: 0.8,
        avatarSpeakingHeadMove: 0.6,
        mixerGainSpeech: 0, // the real voice plays in lib/speech.ts; this one only times the lips
        ...LIGHTS,
      });
      await head.showAvatar(
        { url: MODEL, body: 'F', avatarMood: 'neutral', lipsyncLang: 'en' },
        (e: ProgressEvent) => {
          if (e.lengthComputable) {
            progress = e.loaded / e.total;
            emit();
          }
        },
      );
      tintHair(head);
      host.style.cssText = '';
      status = 'ready';
      progress = 1;
      voiceScript.subscribe(onLine);
      resumeOnGesture();
    } catch (err) {
      console.warn('Elena 3D se nije učitala', err);
      status = 'failed';
    }
    emit();
  })();
  return loading;
}

// The lips are timed by TalkingHead's own (silent) audio clock, which needs a user gesture.
function resumeOnGesture() {
  const resume = () => void head?.audioCtx?.resume();
  for (const ev of ['pointerdown', 'keydown', 'touchstart']) {
    window.addEventListener(ev, resume, { passive: true });
  }
}

let idleTimer: number | undefined;

function onLine() {
  const line: VoiceLine | null = voiceScript.getSnapshot();
  if (!head || status !== 'ready') return;
  window.clearTimeout(idleTimer);
  if (!line) {
    head.stopSpeaking();
    idleTimer = window.setTimeout(() => head?.setMood('neutral'), 2200);
    return;
  }
  head.stopSpeaking();
  const acting = actingFor(line.text);
  const sync = lipSync(line.text, line.durationMs);
  const ctx = head.audioCtx;
  const silent = ctx.createBuffer(
    1,
    Math.max(1, Math.ceil((sync.total / 1000) * ctx.sampleRate)),
    ctx.sampleRate,
  );
  head.setMood(acting.mood);
  if (acting.gesture) head.playGesture(acting.gesture, 2.6, Math.random() < 0.5);
  if (acting.emoji) head.speakEmoji(acting.emoji);
  head.speakAudio({
    audio: silent,
    words: sync.words,
    wtimes: sync.wtimes,
    wdurations: sync.wdurations,
    visemes: sync.visemes,
    vtimes: sync.vtimes,
    vdurations: sync.vdurations,
  });
}

let currentStage: HTMLElement | null = null;
let currentView: AvatarView = 'upper';

/** Moves the one canvas into `stage`. Returns a detach function for unmount. */
export function attachAvatar(stage: HTMLElement, view: AvatarView): () => void {
  if (!host) return () => undefined;
  currentStage = stage;
  stage.appendChild(host);
  if (head && status === 'ready') {
    head.start();
    if (view !== currentView) head.setView(view);
  }
  currentView = view;
  void loadAvatar().then(() => {
    if (currentStage === stage && head && status === 'ready') {
      head.setView(view);
      head.onResize();
    }
  });
  return () => {
    if (currentStage !== stage) return;
    currentStage = null;
    host.remove();
    head?.stop();
  };
}

let lastMode: AvatarMode = 'idle';

/** Listening and thinking show on her face too, not only in the status pill. */
export function setAvatarMode(mode: AvatarMode) {
  if (!head || status !== 'ready' || mode === lastMode) return;
  lastMode = mode;
  if (mode === 'listening') {
    head.makeEyeContact(10_000);
    if (!voiceScript.getSnapshot()) head.setMood('happy');
  } else if (mode === 'thinking') {
    head.speakEmoji('🤔');
  }
}

/** Gaze follows the pointer while she is not speaking. */
export function lookAtPointer(x: number, y: number) {
  if (!head || status !== 'ready' || voiceScript.getSnapshot()) return;
  head.lookAt(x, y, 650);
}
